<script setup lang="ts">
import type { UserNotification } from '../api/types'
import { ElButton, ElTag } from 'element-plus'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCustomToast } from '@/shared/lib'
import { formatDateDisplay } from '@/shared/lib/formatters'
import { CButton, CDialog } from '@/shared/ui'
import { markNotificationAsReadRequest } from '../api/notificationsApi'
import { useNotificationsStore } from '../model/notifications.store'

interface Props {
  notification: UserNotification | null
  modelValue: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const { t } = useI18n()
const { success, error: showError } = useCustomToast()
const notificationsStore = useNotificationsStore()

const isMarkingAsRead = ref(false)

const isVisible = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

function closeModal(): void {
  isVisible.value = false
}

async function handleMarkAsRead(): Promise<void> {
  if (!props.notification || props.notification.is_read) 
    return

  try {
    isMarkingAsRead.value = true
    await markNotificationAsReadRequest({
      notification_ids: [props.notification.id],
    })

    notificationsStore.updateNotificationReadStatus(props.notification.id)
    // Invalidate cache and refresh unread count from API
    notificationsStore.invalidateCache()
    await notificationsStore.refreshUnreadCount()

    success({
      title: t('success'),
      message: t('notification_marked_as_read'),
    })

    // Close modal after marking as read
    closeModal()
  }
  catch (err) {
    showError({
      title: t('error'),
      message: err instanceof Error ? err.message : t('failed_to_mark_as_read'),
    })
  }
  finally {
    isMarkingAsRead.value = false
  }
}

// Close modal when notification becomes null
watch(
  () => props.notification,
  (newNotification) => {
    if (!newNotification) {
      isVisible.value = false
    }
  },
)
</script>

<template>
  <CDialog
    v-model="isVisible"
    :title="notification?.title || $t('notification')"
    class="sm:!w-[500px] !w-[90%]"
  >
    <template #header>
      <h2 class="text-lg font-semibold text-text-primary leading-[26px]">
        {{ notification?.title || $t("notification") }}
      </h2>
    </template>

    <div v-if="notification" class="space-y-4">
      <div>
        <div class="text-sm font-medium text-text-secondary mb-1">
          {{ $t("message") }}
        </div>
        <div
          class="text-sm text-text-primary leading-relaxed whitespace-pre-wrap"
        >
          {{ notification.message }}
        </div>
      </div>

      <div class="flex items-center gap-4 text-xs text-text-secondary">
        <div>
          <span class="font-medium">{{ $t("created_at") }}:</span>
          {{ formatDateDisplay(notification.created_at) }}
        </div>
        <ElTag :type="notification.is_read ? 'info' : 'warning'" size="small">
          {{ notification.is_read ? $t("read") : $t("unread") }}
        </ElTag>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-2">
        <ElButton size="large" class="!h-10 px-6" @click="closeModal">
          {{ $t("close") }}
        </ElButton>
        <CButton
          v-if="notification && !notification.is_read"
          type="primary"
          size="large"
          :loading="isMarkingAsRead"
          class="!h-10 px-6"
          @click="handleMarkAsRead"
        >
          {{ $t("mark_as_read") }}
        </CButton>
        <div v-else class="!h-10 px-6 flex items-center" aria-hidden="true" />
      </div>
    </template>
  </CDialog>
</template>
